'use client';

export type MeasurementTimedStep = 'center-face' | 'place-card' | 'invalid-card';

interface MeasurementTimeoutClock {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => number;
  clearTimeout: (timer: number) => void;
}

const DEFAULT_CLOCK: MeasurementTimeoutClock = {
  now: () => performance.now(),
  setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimeout: (timer) => window.clearTimeout(timer),
};

export interface MeasurementTimeoutController {
  update: (step: MeasurementTimedStep | null) => void;
  setVisible: (visible: boolean) => void;
  reset: () => void;
  dispose: () => void;
}

export function createMeasurementTimeoutController({
  onTimeout,
  timeoutMs = 20_000,
  clock = DEFAULT_CLOCK,
}: {
  onTimeout: (step: MeasurementTimedStep) => void;
  timeoutMs?: number;
  clock?: MeasurementTimeoutClock;
}): MeasurementTimeoutController {
  const elapsed: Record<MeasurementTimedStep, number> = {
    'center-face': 0,
    'place-card': 0,
    'invalid-card': 0,
  };
  let activeStep: MeasurementTimedStep | null = null;
  let activeSince: number | null = null;
  let isVisible = true;
  let timer: number | null = null;
  let disposed = false;

  const clearDeadline = () => {
    if (timer !== null) {
      clock.clearTimeout(timer);
      timer = null;
    }
  };

  const settle = () => {
    if (!activeStep || activeSince === null) return;
    const now = clock.now();
    elapsed[activeStep] += Math.max(0, now - activeSince);
    activeSince = now;
  };

  const scheduleDeadline = () => {
    clearDeadline();
    if (disposed || !isVisible || !activeStep || activeSince === null) return;

    const stepAtSchedule = activeStep;
    const remaining = timeoutMs - elapsed[stepAtSchedule];
    if (remaining <= 0) {
      onTimeout(stepAtSchedule);
      return;
    }

    timer = clock.setTimeout(() => {
      timer = null;
      settle();
      if (!disposed && activeStep === stepAtSchedule && elapsed[stepAtSchedule] >= timeoutMs) {
        onTimeout(stepAtSchedule);
      } else {
        scheduleDeadline();
      }
    }, remaining);
  };

  return {
    update: (nextStep) => {
      if (disposed || nextStep === activeStep) return;
      settle();
      if (activeStep === 'center-face' && nextStep !== 'center-face') {
        elapsed['center-face'] = 0;
      }
      activeStep = nextStep;
      activeSince = isVisible && activeStep ? clock.now() : null;
      scheduleDeadline();
    },
    setVisible: (visible) => {
      if (disposed || visible === isVisible) return;
      if (!visible) {
        settle();
        activeSince = null;
        isVisible = false;
        clearDeadline();
        return;
      }
      isVisible = true;
      activeSince = activeStep ? clock.now() : null;
      scheduleDeadline();
    },
    reset: () => {
      clearDeadline();
      elapsed['center-face'] = 0;
      elapsed['place-card'] = 0;
      elapsed['invalid-card'] = 0;
      activeStep = null;
      activeSince = null;
    },
    dispose: () => {
      disposed = true;
      clearDeadline();
      activeStep = null;
      activeSince = null;
    },
  };
}
